import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import { useRegionLabels } from 'app/shared/hooks/useRegionLabels';
import { RegionDescriptionType } from 'app/shared/model/enumerations/region-description-type.model';
import {Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, ButtonGroup} from 'reactstrap';
import {AvForm, AvGroup, AvField, AvInput} from 'availity-reactstrap-validation';
import {IAmbulance} from 'app/shared/model/ambulance.model';
import {IRegion} from 'app/shared/model/region.model';
import {createEntity as createAmbulance, reset} from 'app/entities/ambulance/ambulance.reducer';
import {getHealthcareFacilitiesCompact} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import {getAllByType as getCountries} from 'app/entities/region/region.reducer';
import {getEntities as getVehicleTypes} from 'app/entities/vehicleType/vehicleType.reducer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';

const radios = [
  { name: 'Active', value: true },
  { name: 'Disabled', value: false }
];

interface AddAmbulanceModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (ambulance: IAmbulance) => void;
  countryId?: string;
}

const AddAmbulanceModalComponent = (props: AddAmbulanceModalProps) => {
  const {isOpen, toggle, onSave, countryId} = props;
  const {healthcareFacilities, loading, updating, vehicleTypes, createSuccess, createdAmbulance} = props;

  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [healthcareFacilitiesByCountry, setHealthcareFacilitiesByCountry] = useState([]);
  const [activeStatus, setActiveStatus] = useState(true);
  const [vehicleTypeList, setVehicleTypeList] = useState([]);

  const filterHealthcareFacilityByCountry = (selectedCountryId: string) => {
    const hfByCountry = healthcareFacilities.filter(hf => hf?.countryId === selectedCountryId);
    const healthcareFacNames = hfByCountry.map((item: any) => ({
      short: item.name,
      full: (item.name + ' / ' + item?.councilName + ' / ' + item?.districtName),
      id: item.id
    }));
    setHealthcareFacilitiesByCountry(healthcareFacNames);
  };

  useEffect(() => {
    if (isOpen) {
      props.reset();
      props.getCountries();
      props.getVehicleTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (props.countries && props.countries.length > 0) {
      const ac = props.countries.find(country =>
        country.id === (countryId || (props.isAdmin ? props.countries[0].id : props.userCountryId))
      );
      if (ac) {
        setActiveCountry(ac);
      }
    }
  }, [props.countries, countryId]);

  useEffect(() => {
    if (vehicleTypes?.length > 0 && activeCountry.id) {
      const filteredTypes = vehicleTypes.filter((item: any) => item.countryId === activeCountry.id);
      setVehicleTypeList(filteredTypes);
    }
  }, [vehicleTypes, activeCountry]);

  useEffect(() => {
    if (activeCountry?.id) {
      if (!healthcareFacilities || healthcareFacilities.length === 0 || healthcareFacilities[0]?.countryId !== activeCountry.id) {
        props.getHealthcareFacilities(activeCountry.id);
      } else {
        filterHealthcareFacilityByCountry(activeCountry.id);
      }
    }
  }, [activeCountry]);

  useEffect(() => {
    if (healthcareFacilities && healthcareFacilities.length > 0 && activeCountry?.id) {
      filterHealthcareFacilityByCountry(activeCountry.id);
    }
  }, [healthcareFacilities]);

  useEffect(() => {
    if (createSuccess && createdAmbulance) {
      onSave(createdAmbulance);
      toggle();
      // Reset form
      setActiveStatus(true);
    }
  }, [createSuccess, createdAmbulance]);

  const onCountryChange = (event) => {
    const selectedCountryId = event.target.value;
    const newActiveCountry = props.countries.find(country => country.id === selectedCountryId);
    setActiveCountry(newActiveCountry);
    const filteredTypes = vehicleTypes.filter((item: any) => item.countryId === selectedCountryId);
    setVehicleTypeList(filteredTypes);
  };

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const entity = {
        ...values,
        active: activeStatus,
        vehicleType: vehicleTypes.find(item => item.id === values.vehicleType),
        driverType: 'CAR'
      };

      props.createAmbulance(entity);
    }
  };

  const healthCareFacilityList = (includeEmpty = false) => {
    let result = (includeEmpty) ? ([{short: '', full: '', id: undefined}]) : [];
    result = result.concat(healthcareFacilitiesByCountry);
    return result;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Add New Ambulance
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <AvForm id="ambulance-form" onSubmit={saveEntity}>
            <AvGroup>
              <Label id="activeStatusLabel">
                Active Status
              </Label>
              <ButtonGroup style={{marginLeft: "30px"}}>
                {radios.map(radio => (
                  <Button
                    key={radio.value.toString()}
                    color={activeStatus === radio.value ? "primary" : "secondary"}
                    onClick={() => setActiveStatus(radio.value)}
                    active={activeStatus === radio.value}
                  >
                    {radio.name}
                  </Button>
                ))}
              </ButtonGroup>
            </AvGroup>

            {props.countries && props.countries.length > 0 && (
              <AvGroup>
                <Label for="ambulance-country">Country</Label>
                <AvInput
                  id="ambulance-country"
                  type="select"
                  className="form-control"
                  name="country"
                  value={activeCountry?.id || ''}
                  onChange={onCountryChange}
                  disabled
                >
                  <option value="">Select a country</option>
                  {props.countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </AvInput>
              </AvGroup>
            )}

            <AvGroup>
              <Label id="driverFirstNameLabel" for="ambulance-driverFirstName">
                Driver First Name
              </Label>
              <AvField
                id="ambulance-driverFirstName"
                type="text"
                name="driverFirstName"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="driverLastNameLabel" for="ambulance-driverLastName">
                Driver Last Name
              </Label>
              <AvField
                id="ambulance-driverLastName"
                type="text"
                name="driverLastName"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="phoneNumberLabel" for="ambulance-phoneNumber">
                Phone Number
              </Label>
              <AvField
                id="ambulance-phoneNumber"
                type="text"
                name="phoneNumber"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                  maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="phone2Label" for="ambulance-phone2">
                Phone Number 2
              </Label>
              <AvField
                id="ambulance-phone2"
                type="text"
                name="phone_number_2"
                validate={{
                  maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="phone3Label" for="ambulance-phone3">
                Phone Number 3
              </Label>
              <AvField
                id="ambulance-phone3"
                type="text"
                name="phone_number_3"
                validate={{
                  maxLength: { value: 15, errorMessage: 'Maximum length is 15 characters' }
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="healthcareFacilityLabel" for="ambulance-healthcareFacility">
                Healthcare Facility
              </Label>
              <VillageAvTypeahead
                labelKey="full"
                valueKey="id"
                data={healthCareFacilityList(false)}
                placeholder="Select Healthcare Facility"
                id="ambulance-healthcareFacility"
                loading={false}
                input={{
                  id: "ambulance-healthcareFacility",
                  name: "healthcareFacilityId",
                  required: true
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="vehicleTypeLabel" for="ambulance-vehicleType">
                Vehicle Type
              </Label>
              <AvInput
                id="ambulance-vehicleType"
                type="select"
                className="form-control"
                name="vehicleType"
                required
              >
                <option value="">Select a vehicle type</option>
                {vehicleTypeList.map(vt => (
                  <option key={vt.id} value={vt.id}>
                    {vt.enType}
                  </option>
                ))}
              </AvInput>
            </AvGroup>

            <AvGroup>
              <Label id="mpesaNumberLabel" for="ambulance-mpesaNumber">
                Mobile Money ID
              </Label>
              <AvField
                id="ambulance-mpesaNumber"
                type="text"
                name="mpesaNumber"
                validate={{
                  maxLength: { value: 10, errorMessage: 'Maximum length is 10 digits' },
                  number: { value: true, errorMessage: 'This field should be a number.' },
                }}
              />
            </AvGroup>
          </AvForm>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <FontAwesomeIcon icon="times" /> Cancel
        </Button>
        <Button
          color="primary"
          type="submit"
          disabled={updating}
          form="ambulance-form"
        >
          <FontAwesomeIcon icon="save" /> Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  healthcareFacilities: storeState.healthcareFacility.compactEntities,
  loading: storeState.ambulance.loading,
  updating: storeState.ambulance.updating,
  createSuccess: storeState.ambulance.updateSuccess,
  createdAmbulance: storeState.ambulance.entity,
  countries: storeState.region.countries,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  vehicleTypes: storeState.vehicleType.entities
});

const mapDispatchToProps = {
  getHealthcareFacilities: getHealthcareFacilitiesCompact,
  createAmbulance,
  reset,
  getCountries,
  getVehicleTypes
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const AddAmbulanceModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(AddAmbulanceModalComponent);

export default AddAmbulanceModal;
